import { useEffect, useState, useRef } from 'react';
import cn from 'classnames';
import isEqual from 'lodash/isEqual';

import { useScheduleYourCallFormContext }
  from '@/evercall/application/scheduleYourCallForm/ScheduleYourCallFormContext';
import Section from '@framework/ui/Pages/Landing/Sections/Section';
import SectionContainer from '@framework/ui/Pages/Landing/Sections/SectionContainer';
import FinalForm from '@framework/ui/Organisms/FinalForm';
import Spinner from '@dealroadshow/uikit-loader/Spinner';
import CallFormContextProvider from '@evercall/call-form/ui/CallFormContext';
import CallFormSteps from '../CallFormSteps';
import Header from './Header';
import FormSuccess from '../FormSuccess';
import getCallDetailsSubmitPayload from '@evercall/call-form/ui/helpers/getCallDetailsSubmitPayload';
import {
  getOaccConfigurationsSubmitPayload,
  getWebinarConfigurationsSubmitPayload,
} from '@evercall/call-form/ui/helpers/getConfigurationsSubmitPayload';
import validate from './validate';
import { mapOptionToValue } from '@framework/data-helpers/selectOptionMappers';

import CallEvent from '@evercall/domain/CallEvent';
import { CallEventType } from '@evercall/domain/vo/call/CallEventType';
import { CallFormInstance } from '@evercall/call-form/ui/interfaces';
import { ICallFormStep, TScheduleCallFormValues, TScheduleCallFormInitialValues } from '../interfaces';

import { SCHEDULE_YOUR_CALL_FORM, CALL_FORM_STEPS } from '../constants';

import styles from './form.module.scss';

const Form = () => {
  const [currentStep, setCurrentStep] = useState<ICallFormStep>(CALL_FORM_STEPS.YOUR_DETAILS);
  const formWrpRef = useRef<HTMLDivElement>(null);
  const {
    state: {
      isInitialized,
      isSubmitting,
      isSubmitSucceeded,
    },
    initScheduleYourCallForm,
    submitScheduleYourOaccCallForm,
    submitScheduleYourWebinarForm,
  } = useScheduleYourCallFormContext();

  useEffect(() => {
    initScheduleYourCallForm();
  }, []);

  const scrollToTop = () => {
    const rect = formWrpRef.current.getBoundingClientRect();
    const offsetTop = rect.top + window.scrollY - 80;
    return window.scrollTo({
      top: offsetTop,
      behavior: 'smooth',
    });
  };

  const submit = (values: TScheduleCallFormValues) => {
    if (!isEqual(currentStep, CALL_FORM_STEPS.ADD_ONS)) {
      const steps = Object.values(CALL_FORM_STEPS);
      const currentStepIndex = steps.indexOf(currentStep);
      setCurrentStep(steps[currentStepIndex + 1]);
      scrollToTop();
    } else {
      const {
        firstName,
        lastName,
        corporateEmail,
        phoneNumber,
        additionalNotes,
        eventTypeId,
      } = values;
      const isWebinar = CallEvent.isWebinar(mapOptionToValue(eventTypeId) as CallEventType);

      if (isWebinar) {
        submitScheduleYourWebinarForm(
          {
            firstName,
            lastName,
            corporateEmail,
            phoneNumber: phoneNumber.value,
            additionalNotes: additionalNotes || '',
            ...getCallDetailsSubmitPayload(values),
            ...getWebinarConfigurationsSubmitPayload(values),
          },
        );
      } else {
        submitScheduleYourOaccCallForm(
          {
            firstName,
            lastName,
            corporateEmail,
            phoneNumber: phoneNumber.value,
            additionalNotes: additionalNotes || '',
            ...getCallDetailsSubmitPayload(values),
            ...getOaccConfigurationsSubmitPayload(values),
          },
        );
      }
    }
  };

  return (
    <CallFormContextProvider instance={ CallFormInstance.ScheduleYourCall }>
      <Section
        padding="top"
        dataTest="formSection"
        className={ styles.form }
      >
        <SectionContainer
          size="middle"
          ref={ formWrpRef }
          className={ cn(styles.formWrapper, { [styles.iSucceeded]: isSubmitSucceeded }) }
        >
          { !isSubmitSucceeded && (<Header currentStep={ currentStep } />) }
          <div className={ styles.contentContainer }>
            <div className={ styles.formContainer }>
              <Spinner
                overlay
                isVisible={ !isInitialized || isSubmitting }
              />
              {
              isSubmitSucceeded
                ? (<FormSuccess />)
                : (
                  <FinalForm<TScheduleCallFormValues, TScheduleCallFormInitialValues>
                    withFocusOnErrors
                    dataTest={ SCHEDULE_YOUR_CALL_FORM }
                    onSubmit={ submit }
                    validate={ (values) => validate(values, currentStep) }
                    render={ () => {
                      if (!isInitialized) {
                        return null;
                      }
                      return (
                        <CallFormSteps
                          scrollToTop={ scrollToTop }
                          currentStep={ currentStep }
                          setCurrentStep={ setCurrentStep }
                        />
                      );
                    } }
                  />
                )
            }
            </div>
          </div>
        </SectionContainer>
      </Section>
    </CallFormContextProvider>
  );
};

export default Form;
